import React, { useCallback, useEffect, useRef, useState } from "react";
import { Check, ImageOff, RotateCcw, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { API_URL, formatApiError } from "../lib/api";
import { PanelBackdrop, publishPanel } from "./AuthHeroPanel";
import { ScheduleForm, ScheduleList } from "./SigninPanelSchedule";

const BLANK_SCHEDULE = { starts_at: "", ends_at: "", label: "" };

const UPLOAD_PREVIEW = `${API_URL}/admin/signin-panel/pending-photo`;

function Preview({ color, photoUrl }) {
  return (
    <div className="relative h-56 w-full max-w-[220px] overflow-hidden border" style={{ background: color, borderColor: "var(--border)" }} data-testid="signin-panel-preview">
      <PanelBackdrop panel={{ color, photo_url: photoUrl }} />
      <div className="relative flex h-full flex-col justify-between p-4 text-[#F7F7F6]">
        <img src="/logo-white.png" alt="" className="h-5 w-auto object-contain self-start" />
        <div>
          <div className="text-[8px] uppercase tracking-[0.2em] mb-2" style={{ color: "#BFD6EC" }}>Client access · Encrypted</div>
          <div className="font-heading text-xl leading-none">Your captive.<br /><b>Clearly measured.</b></div>
        </div>
      </div>
    </div>
  );
}

function Swatches({ colors, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="signin-panel-colors">
      {colors.map((c) => (
        <button key={c.key} type="button" title={`${c.label} ${c.hex}`} onClick={() => onChange(c.key)}
                className="h-10 w-10 border-2 flex items-center justify-center transition-transform duration-150 hover:-translate-y-px"
                style={{ background: c.hex, borderColor: value === c.key ? "var(--accent)" : "transparent" }}
                aria-pressed={value === c.key} data-testid={`signin-panel-color-${c.key}`}>
          {value === c.key && <Check size={14} color="#fff" />}
        </button>
      ))}
    </div>
  );
}

function PhotoTile({ selected, onClick, testId, label, children }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} aria-pressed={selected}
            className="relative h-16 w-24 border-2 overflow-hidden text-[10px] flex items-center justify-center"
            style={{ borderColor: selected ? "var(--accent)" : "var(--border)", color: "var(--muted)" }}>
      {children}
      <span className="absolute bottom-0 inset-x-0 bg-white/85 text-[10px] py-0.5" style={{ color: "var(--ink)" }}>{label}</span>
    </button>
  );
}

export function SigninPanelCard() {
  const [data, setData] = useState(null);
  const [color, setColor] = useState("navy");
  const [photo, setPhoto] = useState("building");
  const [busy, setBusy] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [schedules, setSchedules] = useState([]);
  const [scheduleForm, setScheduleForm] = useState(BLANK_SCHEDULE);
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [{ data: d }, { data: s }] = await Promise.all([
        api.get("/admin/signin-panel"), api.get("/admin/signin-panel/schedules")]);
      setData(d); setColor(d.color); setPhoto(d.photo); setSchedules(s.schedules);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (call, success) => {
    setBusy(true);
    try { const { data: live } = await call(); publishPanel(live); toast.success(success); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Photos must be 10 MB or smaller"); return; }
    const form = new FormData();
    form.append("file", file);
    setBusy(true);
    try {
      await api.post("/admin/signin-panel/photo", form);
      toast.success("Photo uploaded and scanned. Press Save to make it live.");
      setPhoto("upload"); setPreviewKey((k) => k + 1); await load(); setPhoto("upload");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const stopEditing = () => { setEditing(null); setScheduleForm(BLANK_SCHEDULE); setColor(data.color); setPhoto(data.photo); };
  const startEditing = (s) => {
    setEditing(s); setColor(s.color); setPhoto(s.photo);
    setScheduleForm({ starts_at: s.starts_at_eastern, ends_at: s.ends_at_eastern, label: s.label });
  };
  const submitSchedule = async () => {
    const body = { color, photo, ...scheduleForm, ends_at: scheduleForm.ends_at || null };
    setBusy(true);
    try {
      if (editing) await api.put(`/admin/signin-panel/schedules/${editing.id}`, body);
      else await api.post("/admin/signin-panel/schedules", body);
      toast.success(editing ? "Scheduled change updated" : "Change scheduled");
      setEditing(null); setScheduleForm(BLANK_SCHEDULE); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const cancelSchedule = async (s) => {
    if (!window.confirm("Cancel this scheduled change?")) return;
    setBusy(true);
    try { await api.delete(`/admin/signin-panel/schedules/${s.id}`); toast.success("Scheduled change cancelled"); if (editing?.id === s.id) stopEditing(); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const liveSchedule = schedules.find((s) => s.state === "live");
  const uploadPreview = editing?.upload && !data.pending_upload
    ? `${API_URL}/admin/signin-panel/schedules/${editing.id}/photo` : `${UPLOAD_PREVIEW}?k=${previewKey}`;
  const hex = data.colors.find((c) => c.key === color)?.hex || "#003A70";
  const hasUpload = Boolean(data.pending_upload || data.upload || editing?.upload);
  const photoUrl = photo === "none" ? "" : photo === "upload" ? uploadPreview : data.photos.find((p) => p.key === photo)?.url;
  const dirty = color !== data.color || photo !== data.photo || (photo === "upload" && data.pending_upload);

  return (
    <section className="border p-6 mt-4" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="signin-panel-card">
      <p className="text-sm mb-6 max-w-2xl" style={{ color: "var(--muted)" }}>
        The colour and photo behind the left panel on the sign-in, forgot-password, reset-password,
        invitation and emergency-access screens. Changes go live on all of them when you press Save.
      </p>
      <div className="flex flex-wrap gap-10">
        <div className="space-y-6 flex-1 min-w-[280px]">
          <div>
            <div className="field-label mb-2">Colour</div>
            <Swatches colors={data.colors} value={color} onChange={setColor} />
          </div>
          <div>
            <div className="field-label mb-2">Photo</div>
            <div className="flex flex-wrap gap-2" data-testid="signin-panel-photos">
              {data.photos.map((p) => (
                <PhotoTile key={p.key} selected={photo === p.key} onClick={() => setPhoto(p.key)} testId={`signin-panel-photo-${p.key}`} label={p.label}>
                  <img src={p.url} alt="" className="absolute inset-0 h-full w-full object-cover" />
                </PhotoTile>
              ))}
              {hasUpload && (
                <PhotoTile selected={photo === "upload"} onClick={() => setPhoto("upload")} testId="signin-panel-photo-upload" label="Uploaded">
                  <img src={uploadPreview} alt="" className="absolute inset-0 h-full w-full object-cover" />
                </PhotoTile>
              )}
              <PhotoTile selected={photo === "none"} onClick={() => setPhoto("none")} testId="signin-panel-photo-none" label="No photo">
                <ImageOff size={16} />
              </PhotoTile>
            </div>
            <button type="button" className="btn-ghost text-xs mt-3 flex items-center gap-1.5" disabled={busy}
                    onClick={() => fileRef.current?.click()} data-testid="signin-panel-upload-button">
              <Upload size={12} /> Upload a photo (JPG, PNG or WebP, up to 10 MB)
            </button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid="signin-panel-upload-input" />
            {data.pending_upload && (
              <p className="text-xs mt-2" style={{ color: "var(--muted)" }} data-testid="signin-panel-pending-note">
                New upload "{data.pending_upload.filename}" is ready. Select Uploaded and press Save to make it live.
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-primary" disabled={busy || !dirty || Boolean(editing)}
                    onClick={() => run(() => api.put("/admin/signin-panel", { color, photo }), "Sign-in panel updated")}
                    data-testid="signin-panel-save">Save</button>
            <button type="button" className="btn-ghost flex items-center gap-1.5" disabled={busy}
                    onClick={() => { if (window.confirm("Reset the sign-in panel to navy with the building photo?")) run(() => api.post("/admin/signin-panel/reset"), "Sign-in panel reset to default"); }}
                    data-testid="signin-panel-reset"><RotateCcw size={14} /> Reset to default</button>
          </div>
          {data.updated_at && (
            <p className="text-xs" style={{ color: "var(--muted)" }} data-testid="signin-panel-updated">
              Last changed {new Date(data.updated_at).toLocaleString()} by {data.updated_by}
            </p>
          )}
          {liveSchedule && (
            <p className="text-xs p-2 border" style={{ borderColor: "#E8A838", background: "#FFF7E6", color: "var(--text)" }} data-testid="signin-panel-live-schedule-note">
              A scheduled change is live now{liveSchedule.ends_at_eastern ? " until it ends" : ""}. Saving above changes the everyday panel shown after it ends.
            </p>
          )}
          <ScheduleForm form={scheduleForm} setForm={setScheduleForm} editing={Boolean(editing)} busy={busy}
                        onSubmit={submitSchedule} onStopEditing={stopEditing} />
          <ScheduleList schedules={schedules} colors={data.colors} photos={data.photos} busy={busy}
                        onEdit={startEditing} onCancel={cancelSchedule} />
        </div>
        <div>
          <div className="field-label mb-2">Preview</div>
          <Preview color={hex} photoUrl={photoUrl} />
        </div>
      </div>
    </section>
  );
}
